import * as Sentry from '@sentry/vue'
import { AGREEMENT_DECLINED_CODE } from '@/utils/agreementGatePrompt'
import { redirectToDpaAccept, redirectToTermsAccept } from '@/utils/agreementGateRedirect'
import { API_BASE } from '@/utils/api'
import { requestPasskeySudo } from '@/utils/passkeySudoPrompt'
import { hostSlug } from '@/utils/platformHost'
import { redactApiPath } from '@/utils/redactApiPath'

/**
 * Typed error for API responses. Distinguishes domain errors (4xx)
 * from server errors (5xx) so Sentry only captures the latter.
 */
// Loose body shape — `error` is the only field every endpoint
// promises, but specific 4xx responses (seat_limit_reached → used,
// max, pending; plan_feature_required → feature) ride extra keys
// the SPA dispatches on. Index-signature lets callers read them
// without casting away the type altogether.
export interface ApiErrorBody {
  error?: string
  [k: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.error ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }

  /** True for 4xx errors — domain/validation, should NOT go to Sentry */
  get isDomainError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
}

// Browser-level fetch rejections (offline transitions, iOS Safari
// backgrounding, ad-block, CORS preflight aborted, mobile network
// flips) surface as a TypeError with one of a small set of message
// strings. They aren't actionable as individual Sentry exceptions —
// there's no backend stack — and the calendar view's parallel
// Promise.allSettled fan-out turns one transient blip into 6+ issue
// groups. Sentry Uptime on /health is the load-bearing signal for
// "API is actually down"; this branch keeps frequency/path visibility
// without paging one issue per failed parallel call.
const BROWSER_NETWORK_FAILURE_PATTERNS = ['Failed to fetch', 'Load failed', 'NetworkError']

function isBrowserNetworkFailure(err: unknown, path: string): err is TypeError {
  if (!(err instanceof TypeError)) return false
  if (!path.startsWith('/api/')) return false
  return BROWSER_NETWORK_FAILURE_PATTERNS.some((p) => err.message.includes(p))
}

// Best-effort host tag for the aggregate message — never throw out
// of the error path just because URL parsing failed (tests with an
// empty API_BASE, exotic SSR boots, etc.).
function safeHost(base: string): string {
  try {
    return new URL(base || (typeof window !== 'undefined' ? window.location.origin : '')).host
  } catch {
    return 'unknown'
  }
}

// --- CSRF ---
//
// Two-tier source of truth:
//   1. In-memory token captured from /api/admin/* response bodies
//      (login flows + /me + /refresh all include `csrf_token` in
//      the JSON envelope). This is the working source for the
//      cross-origin case where the SPA at *.stemne.no calls the
//      API at api.stemne.no — auth cookies are host-only on the
//      API host (PR #216, intentional for cross-eTLD support),
//      so document.cookie on the SPA's origin never sees them.
//   2. document.cookie fallback for same-origin setups (signup
//      flow on stemne.no apex, customer portal on the tenant
//      subdomain). Older shape — kept so existing flows that
//      depended on it don't regress.
//
// captureCsrf() runs after every successful JSON response and
// upserts the in-memory value if the payload carries a
// csrf_token field. That covers every backend handler that
// mints a new token: login.finish, magic-link verify, refresh,
// transfer-token exchange, signup verify, and /me on mount.

let inMemoryCsrf = ''

function captureCsrf(payload: unknown): void {
  if (!payload || typeof payload !== 'object') return
  const token = (payload as { csrf_token?: unknown }).csrf_token
  if (typeof token === 'string' && token !== '') {
    inMemoryCsrf = token
  }
}

function getCsrfToken(): string {
  if (inMemoryCsrf) return inMemoryCsrf
  // Multiple csrf_token cookies can coexist when a parent-domain mint
  // shadows the child (e.g. a leftover Domain=stemne.no cookie from a
  // prod visit shadowing a fresh Domain=qa.stemne.no cookie on QA). The
  // browser sends both on every request and document.cookie returns
  // both — but we can't tell which one matches the server's
  // access_token without trying. Return empty so the caller's refresh
  // bootstrap fires, which pulls the fresh csrf_token via captureCsrf
  // on a typed JSON response — that round-trip is the disambiguator.
  // Backend eviction in admin.evictStaleParentCookies + signup's copy
  // covers the cleanup after the next mint.
  const matches = document.cookie.matchAll(/(?:^|;\s*)csrf_token=([^;]+)/g)
  const values: string[] = []
  for (const m of matches) {
    if (m[1]) values.push(m[1])
  }
  if (values.length !== 1) return ''
  return values[0]
}

// Exposed for tests + the logout flow, which needs to clear the
// in-memory value so a subsequent login captures the fresh one.
export function clearCsrfToken(): void {
  inMemoryCsrf = ''
}

// --- Token refresh ---

// Endpoints that establish, rotate, or revoke a session — refresh-
// and-retry on 401 must NOT fire here. Either there's no session to
// refresh yet (login/magic-link), the call IS the refresh (refresh,
// token-exchange consumes a single-use transfer token), or the call
// consumes a single-use cookie that the retry would invalidate
// (webauthn login/register).
//
// Do NOT add normal authenticated mutations here even if they involve
// auth concepts (e.g. /transfer-token mints a cross-origin token but
// rides the existing session and benefits from the auto-retry).
const RETRY_ON_401_EXCLUDED = [
  '/api/admin/webauthn/login',
  '/api/admin/webauthn/register',
  '/api/admin/magic-link',
  '/api/admin/refresh',
  '/api/admin/token-exchange',
  '/api/admin/logout',
]

// Pre-flight CSRF bootstrap exclusions — narrower than the 401-retry
// list. Bootstrap calls /refresh BEFORE the request to mint a CSRF
// token; it doesn't touch single-use challenge cookies, so endpoints
// that 401-retry would invalidate are *not* automatically also unsafe
// for bootstrap.
//
// What stays here:
//   - /refresh: the bootstrap IS this call. Avoid double-invocation.
//   - /magic-link, /webauthn/login: no session exists yet, refresh
//     would 401 harmlessly — skip to save the round trip. These
//     endpoints also have no CSRF middleware on the backend, so no
//     header is needed anyway.
//   - /token-exchange: consumes a single-use cross-origin token; no
//     prior session to refresh from.
//   - /logout: clearing — fresh CSRF is moot.
//
// What was REMOVED: /webauthn/register. It runs in the passkey-
// enrollment group which DOES require CSRF, and the caller has an
// active magic_link session that /refresh can rotate. Without the
// bootstrap, every fresh-page passkey enrollment 403s on cross-
// origin SPAs that haven't yet hit /me to capture a CSRF token.
const CSRF_BOOTSTRAP_EXCLUDED = [
  '/api/admin/webauthn/login',
  '/api/admin/magic-link',
  '/api/admin/refresh',
  '/api/admin/token-exchange',
  '/api/admin/logout',
]

function isRetryOn401Excluded(path: string): boolean {
  return RETRY_ON_401_EXCLUDED.some((prefix) => path.startsWith(prefix))
}

function isCsrfBootstrapExcluded(path: string): boolean {
  return CSRF_BOOTSTRAP_EXCLUDED.some((prefix) => path.startsWith(prefix))
}

// O3g (issue #1272 follow-up). tryRefresh returns a tri-state outcome
// so the caller can distinguish three cases:
//
//   1. refreshed=true              — refresh succeeded, retry the call
//   2. refreshed=false, rejected=true  — server actively rejected the
//      refresh (401). Session is dead. The 401 caller invalidates
//      useCurrentAdmin so any cached identity flips to null and any
//      "Gå til dashboard" CTA reverts to "Logg inn" on the next render.
//   3. refreshed=false, rejected=false — transient (5xx, network blip,
//      403/404 etc.). The session MIGHT still be valid; the caller
//      should NOT invalidate the cache because a transient backend
//      issue must not flicker UI to "logged out".
//
// Strictly 401 is the rejection signal — agent flagged that 5xx/network
// errors used to collapse into the same `return false` as 401, which
// would over-aggressively flush the cache on every mobile network hiccup.
type RefreshOutcome = {
  refreshed: boolean
  rejected: boolean
}

let refreshPromise: Promise<RefreshOutcome> | null = null

async function tryRefresh(): Promise<RefreshOutcome> {
  // /api/admin/refresh is mounted inside the tenant-resolver group
  // (routes.go:110), so it needs X-Tenant-Slug just like every other
  // admin call. The mainline request() helper threads the slug
  // automatically; this raw-fetch path must do it explicitly or the
  // resolver 404s before the refresh handler ever runs. Symptom: a
  // simple "401 → tryRefresh → 404 → redirect to /login" cascade on
  // every expired access_token, which looked like a CSRF bug because
  // the user landed somewhere unexpected with stale state.
  const headers: Record<string, string> = {}
  const slug = getTenantSlug()
  if (slug) headers['X-Tenant-Slug'] = slug
  try {
    const res = await fetch(`${API_BASE}/api/admin/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers,
    })
    if (!res.ok) {
      // 401 = refresh explicitly rejected → session dead.
      // Anything else (403/404/5xx) = treat as transient; don't
      // invalidate the cache on the strength of a backend issue.
      return { refreshed: false, rejected: res.status === 401 }
    }
    // /refresh returns the same JSON envelope as login (carries the
    // rotated csrf_token). Capture so the retried request has a
    // valid header to send. Without this the auto-refresh succeeds
    // but the retry then 403s on CSRF for cross-origin SPAs.
    const json = await res.json().catch(() => null)
    captureCsrf(json)
    return { refreshed: true, rejected: false }
  } catch {
    // Network error / fetch threw — transient. The user might be
    // offline or the backend might be unreachable; either way the
    // cache must not flip to "logged out" on the strength of a
    // network hiccup.
    return { refreshed: false, rejected: false }
  }
}

// invalidateAdminIdentity dynamically imports useCurrentAdmin to avoid
// a circular module dependency (useCurrentAdmin imports `api` from
// this file). Called only on the confirmed-session-rejected branch of
// the 401 handler; the dynamic-import cost is paid once per real
// session-revoke event, which is rare.
async function invalidateAdminIdentity(): Promise<void> {
  try {
    const mod = await import('@/composables/useCurrentAdmin')
    mod.invalidateCurrentAdmin()
  } catch {
    // Best-effort. The cache flushing is a UX improvement, not a
    // security boundary — the server is already going to reject any
    // request the stale cache would have allowed.
  }
}

// sessionTeardown imports the calendar store, which imports this client.
// Resolve it only after the server has explicitly rejected refresh so the
// dependency cycle never exists during module initialization.
async function clearRejectedSessionLocalData(): Promise<void> {
  try {
    const mod = await import('@/utils/sessionTeardown')
    mod.clearOperatorLocalData()
  } catch {
    // Best-effort: a teardown failure must not prevent the login redirect.
  }
}

// --- Core request ---

// Extract the tenant slug from the page hostname. Production:
// `kodelauget.stemne.no` → `kodelauget`. Marketing/apex (`stemne.no`)
// has only two labels, no slug.
//
// Onboarding fallback: when the operator is mid-onboarding on apex (we
// don't redirect them to {slug}.stemne.no until Cloudflare confirms
// cert provisioning is done), they still need /api/admin/* calls to
// resolve to their tenant. We pull the just-claimed slug from the
// onboarding session store. After CF is ready, the redirect to
// {slug}.stemne.no makes the host-based path take over again.
// Exported for use by WS composables — browser WebSocket
// constructors can't set custom headers (X-Tenant-Slug), so the
// SPA must pass the slug as a ?tenant= URL parameter when
// connecting to wss://api.<root>. Same source of truth as the
// HTTP path; here, just exposed.
export function getTenantSlug(): string {
  // hostSlug strips the configured apex (stemne.no / qa.stemne.no / localhost)
  // and returns the leading tenant label, or '' on the apex. On the apex,
  // fall back to the onboarding store.
  return hostSlug() || getOnboardingSlug()
}

// getOnboardingSlug reads sessionStorage directly — pulling in the
// useOnboarding composable here would create a Vue runtime dep on the
// API client, which is module-loaded before any Vue setup. Reading the
// raw key keeps this lookup synchronous and lifecycle-independent.
function getOnboardingSlug(): string {
  try {
    const raw = sessionStorage.getItem('stemne_onboarding')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { subdomain?: string }
    return parsed.subdomain ?? ''
  } catch {
    return ''
  }
}

// newIdempotencyKey mints a token for one logical write attempt.
//
// crypto.randomUUID is available in every browser the app supports; the
// counter fallback keeps unit tests (and any non-secure context) working
// rather than throwing. Uniqueness only has to hold within one client
// session — the server namespaces the key by org, route and caller.
let idempotencyCounter = 0
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  idempotencyCounter += 1
  return `fallback-${Date.now()}-${idempotencyCounter}`
}

async function request<T>(path: string, opts: RequestOptions = {}, isRetry = false): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opts.headers as Record<string, string>) ?? {}),
  }

  const isMutation = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS'
  const isAdminPath = path.startsWith('/api/admin') || path.startsWith('/api/staff')

  // Bootstrap CSRF before mutating admin/staff calls when the in-memory
  // token is empty and document.cookie doesn't have one either. This
  // covers the cross-origin SPA case: the SPA at {slug}.stemne.no
  // can't see host-only cookies set on api.stemne.no, so unless a
  // prior response body echoed `csrf_token` into memory, the header
  // we'd send is empty and the backend 403s "invalid CSRF token".
  // /refresh rotates access + csrf and `captureCsrf` puts the rotated
  // value into memory, so the actual request can proceed. Auth
  // endpoints and the refresh call itself opt out for the usual
  // reasons (single-use, no session yet, etc.).
  if (isMutation && isAdminPath && !isRetry && !isCsrfBootstrapExcluded(path) && !getCsrfToken()) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    await refreshPromise
  }

  // Add CSRF header for mutating requests
  if (isMutation) {
    const csrf = getCsrfToken()
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  // Idempotency-Key on mutating admin/staff calls (#3306).
  //
  // The key is PER ATTEMPT, not per user action: two clicks are two request()
  // calls and therefore two different keys. That is deliberate but it bounds
  // what this header can do — it identifies one in-flight attempt across an
  // internal retry, and nothing more. Routes that must survive a double-click
  // are mounted with middleware.IdempotencyBodyFallback, which keys on the
  // request body and ignores this header for exactly that reason. Do not
  // reach for this key to explain why a route is double-submit-safe.
  //
  // The key is stamped onto `opts.headers`, NOT just onto the local `headers`
  // object, because the 401-refresh path re-enters this function with the same
  // `opts`. A retry is the same logical attempt and MUST carry the same key —
  // regenerating it there would make the guard useless in the exact case it
  // exists for. Same reason it is generated once per request() call rather
  // than per fetch.
  if (isMutation && isAdminPath) {
    const existing = (opts.headers as Record<string, string> | undefined)?.['Idempotency-Key']
    const key = existing ?? newIdempotencyKey()
    headers['Idempotency-Key'] = key
    opts.headers = { ...((opts.headers as Record<string, string>) ?? {}), 'Idempotency-Key': key }
  }

  // Tag the request with the tenant slug derived from the page host.
  // The backend's tenant resolver reads this when the request lands on
  // the dedicated api.<root> host (i.e. when the SPA and API live on
  // different subdomains under the same registrable domain).
  const slug = getTenantSlug()
  if (slug) headers['X-Tenant-Slug'] = slug

  // Portal CSRF defence-in-depth. /api/portal/* uses an HttpOnly
  // session cookie auto-attached by the browser; SameSite=Lax already
  // blocks cross-origin POST, but the server requires X-Stemne-Portal: 1
  // on POSTs as an extra gate (any cross-origin request setting a
  // custom header triggers a CORS preflight, blocking the attack).
  // Set on all portal calls so reads also stay consistent — it's
  // free and the server ignores it on GETs.
  if (path.startsWith('/api/portal')) headers['X-Stemne-Portal'] = '1'

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    })
  } catch (networkError) {
    if (isBrowserNetworkFailure(networkError, path)) {
      // Aggregate under one stable fingerprint — frequency + affected
      // paths stay visible in tags/extra, but Sentry collapses the burst
      // into one warning issue instead of one exception per call.
      Sentry.captureMessage('api network request failed', {
        level: 'warning',
        tags: {
          'api.method': method,
          'api.host': safeHost(API_BASE),
        },
        extra: {
          apiPath: redactApiPath(path),
          errorName: networkError.name,
          errorMessage: networkError.message,
        },
        fingerprint: ['api-network-request-failed'],
      })
    } else {
      Sentry.captureException(networkError, {
        tags: { 'api.path': redactApiPath(path), 'api.method': method },
      })
    }
    throw networkError
  }

  // On 401 from admin endpoints: try refreshing the access token once.
  //
  // Auth endpoints opt out — refreshing during a login/refresh/magic-link
  // flow is nonsensical (there's no session yet, or the call IS the
  // session-establishing call). Worse, retrying single-use endpoints
  // like /webauthn/login/finish consumes the challenge cookie and
  // causes the second call to return a misleading "session expired"
  // error that masks the real auth failure.
  // Refresh-and-retry covers both operator routes (/api/admin) and the
  // staff console (/api/staff) — both share the AdminAuth chain, so the
  // same access-token cookie expires for both.
  if (res.status === 401 && !isRetry && isAdminPath && !isRetryOn401Excluded(path)) {
    // Coalesce concurrent refreshes into one request
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    const outcome = await refreshPromise
    if (outcome.refreshed) {
      return request<T>(path, opts, true)
    }
    // Both invalidation AND the hard-redirect to /auth/login are gated
    // on outcome.rejected — i.e. the refresh endpoint explicitly returned
    // 401 ("session is genuinely dead"). Transient failures (5xx,
    // network blip, CORS preflight blip, AbortError from a parallel
    // navigation) leave the cache alone AND don't bounce the operator
    // to login — they keep the user signed in and let the next action
    // retry. Pre-this gate, the redirect fired whenever
    // outcome.refreshed===false regardless of `rejected`, which silently
    // logged operators out on every transient refresh failure with NO
    // server-side trace (the live refresh row was never even queried).
    // See 2026-06-14 incident: 3 forced logouts in 27 min on prod with
    // healthy DB session state — root-caused to this exact code path.
    if (outcome.rejected) {
      await invalidateAdminIdentity()
      await clearRejectedSessionLocalData()
      // /staff has its own redirect target because the staff console
      // runs on the apex (stemne.no). The tenant /auth/login route
      // doesn't exist there; landing on apex /login skips a router
      // bounce. We also preserve ?next= so the user can be sent back
      // after re-auth (the round-trip back to /staff is not yet wired
      // end-to-end — see the staff guard comment in router/index.ts).
      const here = window.location.pathname
      if (here.startsWith('/staff')) {
        const next = here + window.location.search
        window.location.href = `/login?next=${encodeURIComponent(next)}`
        throw new ApiError(401, { error: 'session expired' })
      }
      if (here.startsWith('/dashboard') || here.startsWith('/setup')) {
        // #1430 — preserve the current path as ?next= so LoginView can
        // route back to where the operator was after re-auth. The
        // router beforeEach guard already builds an `/auth/login?next=`
        // URL on direct nav, but its soft-redirect loses the race to
        // this hard window.location when the guard's /me probe is what
        // returned 401 in the first place. Use the same encoding the
        // /staff branch above uses.
        const next = here + window.location.search
        window.location.href = `/auth/login?next=${encodeURIComponent(next)}`
        throw new ApiError(401, { error: 'session expired' })
      }
    } else {
      // Transient refresh failure — fingerprinted as a single Sentry
      // issue so we can see the rate this fires in prod. Mirrors the
      // api-network-request-failed aggregation a few lines up. Closes
      // the SPA-side observability gap from the 2026-06-14 incident,
      // where the bounce left no server-side audit row and we had to
      // reconstruct the cause from cookie attributes after the fact.
      Sentry.captureMessage('apiClient: transient refresh failure', {
        level: 'warning',
        fingerprint: ['apiClient-transient-refresh'],
        extra: { path: redactApiPath(path), status: res.status },
      })
    }
  }

  // On 403 from /api/staff/*: the operator has an active session but
  // is no longer a staff member (revoked, disabled, or rotated). The
  // router guard handles this on fresh navigation by silently bouncing
  // to /. Mirror that behaviour here so a session that loses staff
  // privileges mid-page also bounces — otherwise the user is stuck
  // looking at a half-rendered console with /me returning 403.
  //
  // BUT: sudo_required / staff_passkey_required are expected 403s from
  // the inline sudo-elevation flow (useSudoAction). They must reach the
  // caller so the footer can transition state. Pre-fix this block ate
  // them too, which silently bounced any fresh destructive action on
  // the staff console to the apex and lost the in-flight form state.
  if (
    res.status === 403 &&
    path.startsWith('/api/staff') &&
    window.location.pathname.startsWith('/staff')
  ) {
    const peek = await res
      .clone()
      .json()
      .catch(() => ({}) as { error?: string })
    const code = (peek as { error?: string }).error
    if (code !== 'sudo_required' && code !== 'staff_passkey_required') {
      window.location.href = '/'
      throw new ApiError(403, { error: 'not staff' })
    }
  }

  // On 403 "passkey_sudo_required" (#1042 / #1190): the operator's 5-minute
  // sudo window has lapsed for passkey rename/remove. Open a global
  // re-auth modal; on successful WebAuthn assertion, retry the original
  // request transparently so the calling component never sees the 403.
  // On cancel / failure, fall through and surface the 403 normally so the
  // caller can render inline error state.
  //
  // register/finish is deliberately EXCLUDED: it 403s while the
  // AddPasskeyModal is still open, and a global modal stacked on that
  // drawer is unclickable (focus-trap + body pointer-events lock) — the
  // operator spins forever. That flow handles the 403 inline via
  // useAddPasskey instead (the footer becomes "Bekreft med passnøkkel"),
  // so the 403 must reach the caller untouched.
  if (
    res.status === 403 &&
    path.startsWith('/api/admin') &&
    !path.endsWith('/webauthn/register/finish') &&
    !isRetry
  ) {
    const peek = await res
      .clone()
      .json()
      .catch(() => ({}) as { error?: string })
    if (peek.error === 'passkey_sudo_required') {
      const ok = await requestPasskeySudo()
      if (ok) {
        return request<T>(path, opts, true)
      }
      // Re-auth cancelled or failed — surface the original 403.
      throw new ApiError(403, peek)
    }
  }

  // On 403 "passkey_required": redirect to passkey enrollment page
  if (res.status === 403 && path.startsWith('/api/admin')) {
    const peek = await res
      .clone()
      .json()
      .catch(() => ({}))
    if (peek.error === 'passkey_required' && !window.location.pathname.includes('/auth/passkey')) {
      window.location.href = '/auth/passkey'
      throw new ApiError(403, peek)
    }
    // Trademark soft-reserve gate (#1063). Backend returns
    // 403 awaiting_review on every operator data route until staff
    // approve/reject. Route to the dedicated interstitial; don't
    // loop the redirect when already on /auth/awaiting-review.
    if (
      peek.error === 'awaiting_review' &&
      !window.location.pathname.includes('/auth/awaiting-review')
    ) {
      window.location.href = '/auth/awaiting-review'
      throw new ApiError(403, peek)
    }
    // Workspace mismatch: the session's cookie is apex-shared, but its
    // JWT belongs to a different org than the one this host serves —
    // AdminAuth refuses data with a typed 403 instead of rendering org
    // A's data under org B's URL. Route to the wrong-workspace
    // interstitial where the operator chooses between their own
    // workspace and logging in here. Same destination as the router
    // guard's checkTenantMismatch, so whichever fires first wins.
    if (peek.error === 'wrong_workspace') {
      redirectToSessionWorkspace()
      throw new ApiError(403, peek)
    }
    // Databehandleravtale gate (#1887). The router bounce only runs on a
    // navigation; when the gate flips UNDER a live session (a DPA version
    // bump stales every existing acceptance) nothing navigates and the
    // operator sits on a dashboard whose every call 403s — rendered as
    // scattered "ingen tilgang" fragments. Route to the accept screen from
    // the response instead, so the gate surfaces the moment it engages.
    if (peek.error === 'dpa_required') {
      // Await the confirmation before rejecting (#3287). Rejecting first would
      // hand the caller its error while the modal is still open, so a caller
      // that renders `errorMessage(e)` would leave "Ingen tilgang" sitting
      // underneath the prompt and still showing after the operator pressed
      // Avbryt. The call stays pending while they decide — the same shape the
      // passkey sudo prompt above uses.
      if (!(await redirectToDpaAccept())) {
        throw new ApiError(403, { ...peek, code: AGREEMENT_DECLINED_CODE })
      }
      throw new ApiError(403, peek)
    }
    // Operatøravtale gate (#3025 P1). Same reasoning, its own discriminator:
    // the two agreements version independently, so the backend has to be able
    // to say which one is outstanding, and the SPA has to invalidate the
    // matching cache. Both land on the same blocking screen.
    if (peek.error === 'terms_required') {
      if (!(await redirectToTermsAccept())) {
        throw new ApiError(403, { ...peek, code: AGREEMENT_DECLINED_CODE })
      }
      throw new ApiError(403, peek)
    }
  }

  // On 402 "subscription_inactive": route to the billing tab so the
  // operator can recover by paying. Don't loop the redirect when
  // already on /dashboard/settings (where the tab lives) — the
  // BillingTab + a global banner handle the in-page case.
  if (res.status === 402 && path.startsWith('/api/admin')) {
    const peek = await res
      .clone()
      .json()
      .catch(() => ({}))
    if (peek.error === 'subscription_inactive') {
      const onSettings = window.location.pathname.startsWith('/dashboard/settings')
      if (!onSettings) {
        // Tab=billing keeps the URL deep-link-friendly; SettingsView
        // reads the tab param on mount.
        window.location.href = '/dashboard/settings?tab=billing'
      }
      // Throw the typed error either way so the calling component's
      // catch sees it (it can show a toast, the banner, etc.).
      throw new ApiError(402, peek)
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const error = new ApiError(res.status, body)

    // Only capture 5xx to Sentry — 4xx are domain/validation errors
    if (!error.isDomainError) {
      Sentry.captureException(error, {
        tags: { 'api.path': redactApiPath(path), 'api.method': method },
      })
    }

    throw error
  }

  if (res.status === 204) return undefined as T

  const json = await res.json()
  // Capture csrf_token from any /api/admin/* response that carries
  // one (login flows, /me, /refresh, transfer-token). Centralised
  // here so callers don't each need to remember to thread it.
  if (path.startsWith('/api/admin') || path.startsWith('/api/signup')) {
    captureCsrf(json)
  }
  return json
}

// requestBlob mirrors request<T> but returns the raw Blob + a hint
// at the filename pulled out of Content-Disposition. Used for binary
// downloads (PDF export, future ICS/CSV) where parsing JSON would
// throw. Shares the CSRF + 401 refresh plumbing with the typed
// path by delegating the up-front bootstrap into a shared fetch
// preamble — without that, a 401 mid-download would silently fail
// instead of re-auth-and-retry.
async function requestBlob(
  path: string,
  opts: RequestOptions = {},
  isRetry = false,
): Promise<{ blob: Blob; filename: string | null }> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = {
    ...((opts.headers as Record<string, string>) ?? {}),
  }
  const isMutation = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS'
  const isAdminPath = path.startsWith('/api/admin') || path.startsWith('/api/staff')
  if (isMutation && isAdminPath && !isRetry && !isCsrfBootstrapExcluded(path) && !getCsrfToken()) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    await refreshPromise
  }
  if (isMutation) {
    const csrf = getCsrfToken()
    if (csrf) headers['X-CSRF-Token'] = csrf
  }
  const slug = getTenantSlug()
  if (slug) headers['X-Tenant-Slug'] = slug

  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers,
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  })

  if (res.status === 401 && !isRetry && isAdminPath && !isRetryOn401Excluded(path)) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    const outcome = await refreshPromise
    if (outcome.refreshed) return requestBlob(path, opts, true)
    // O3g — confirmed-rejected refresh → invalidate cached identity.
    // Same semantics as the JSON path above. requestBlob doesn't
    // perform the SPA redirect; the throw propagates.
    if (outcome.rejected) {
      await invalidateAdminIdentity()
    }
    throw new ApiError(401, { error: 'session expired' })
  }

  if (!res.ok) {
    // Error envelopes are JSON even for would-be binary endpoints;
    // try to parse so the caller can render the inline message.
    const body = await res.json().catch(() => ({}))
    const error = new ApiError(res.status, body)
    if (!error.isDomainError) {
      Sentry.captureException(error, {
        tags: { 'api.path': redactApiPath(path), 'api.method': method },
      })
    }
    throw error
  }

  // Pull filename out of Content-Disposition so the caller can name
  // the download. RFC 6266 has a richer form (filename*=UTF-8''…)
  // but the backend writes plain ASCII, so we just grep the simple
  // form. A missing header surfaces as null and the caller falls
  // back to a sensible default.
  const cd = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(cd)
  const filename = match ? match[1] : null

  const blob = await res.blob()
  return { blob, filename }
}

// requestRaw sends a File/Blob body verbatim (no JSON encoding) with the
// file's own Content-Type, and parses a typed JSON response. Used by the
// encrypted-upload path, where the request body IS the raw plaintext file and
// the server reads Content-Type + Content-Length to size + classify it. Shares
// the CSRF-bootstrap + 401-refresh plumbing with request<T>/requestBlob so a
// mid-upload token expiry re-auths instead of failing. The browser sets
// Content-Length from the Blob automatically.
async function requestRaw<T>(path: string, file: File, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
  }
  const isAdminPath = path.startsWith('/api/admin') || path.startsWith('/api/staff')
  if (isAdminPath && !isRetry && !isCsrfBootstrapExcluded(path) && !getCsrfToken()) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    await refreshPromise
  }
  const csrf = getCsrfToken()
  if (csrf) headers['X-CSRF-Token'] = csrf
  const slug = getTenantSlug()
  if (slug) headers['X-Tenant-Slug'] = slug

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: file,
  })

  if (res.status === 401 && !isRetry && isAdminPath && !isRetryOn401Excluded(path)) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => {
        refreshPromise = null
      })
    }
    const outcome = await refreshPromise
    if (outcome.refreshed) return requestRaw<T>(path, file, true)
    if (outcome.rejected) await invalidateAdminIdentity()
    throw new ApiError(401, { error: 'session expired' })
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const error = new ApiError(res.status, body)
    if (!error.isDomainError) {
      Sentry.captureException(error, {
        tags: { 'api.path': redactApiPath(path), 'api.method': 'POST' },
      })
    }
    throw error
  }

  if (res.status === 204) return undefined as T
  const json = await res.json()
  if (path.startsWith('/api/admin')) captureCsrf(json)
  return json
}

// probe() is the deliberately-dumb counterpart to request<T>: it threads
// the same X-Tenant-Slug header + credentials:'include' that every admin/
// staff call needs, but it does NOT 401-refresh-and-retry, does NOT
// hard-redirect on auth failure, does NOT touch CSRF, and does NOT report
// to Sentry. It returns the auth outcome as data (`ok` / `status`) so the
// caller decides what to do.
//
// This exists for the router guards and useIsStaff, which intentionally
// want a non-200 (401/403) to be a *value* they branch on — "no session →
// SPA-redirect to /login", "authenticated-but-not-staff → silent bounce to
// /" — rather than letting the mainline client hard-navigate via
// window.location mid-guard or flush the cached identity. Routing those
// probes through api.get would change their control flow; routing them
// through fetch() re-pastes the slug+credentials plumbing this client
// exists to own. probe() is the middle ground: shared plumbing, caller-
// owned outcome.
//
// Result mirrors the slice of Response the callers actually read: `ok`,
// `status`, and a lazy `json()` (best-effort, resolves to null on a body
// that doesn't parse). On a network throw, `ok` is false and `status` is 0
// so the caller's catch-equivalent branch (treat as failure) still fires
// without a try/catch around the call.
export interface ProbeResult {
  ok: boolean
  status: number
  json: <T = unknown>() => Promise<T | null>
}

async function probe(path: string, opts: RequestOptions = {}): Promise<ProbeResult> {
  const headers: Record<string, string> = {
    ...((opts.headers as Record<string, string>) ?? {}),
  }
  const slug = getTenantSlug()
  if (slug) headers['X-Tenant-Slug'] = slug
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    })
    return {
      ok: res.ok,
      status: res.status,
      json: async <T>() => (await res.json().catch(() => null)) as T | null,
    }
  } catch {
    // Network throw (offline, CORS preflight aborted, etc.) — surface as a
    // failed probe. The callers treat any non-ok identically to a thrown
    // fetch, so collapsing the two here keeps their branch logic simple.
    return { ok: false, status: 0, json: async () => null }
  }
}

// --- Workspace mismatch (wrong_workspace) ---
//
// Fires when the backend's AdminAuth workspace-match guard refuses a
// data call because the session's JWT belongs to a different org than
// the one this host serves. Apex-shared cookies make this reachable by
// simply visiting another tenant's subdomain while signed in — e.g. a
// sales-demo session wandering onto a real tenant's /admin URL.
//
// Route to the /auth/wrong-workspace interstitial (same destination
// the router guard's checkTenantMismatch uses, so guard and client
// can't fight). The view resolves the session's workspace from /me
// (allow-listed under mismatch) and offers "continue there" or "log
// out and log in here" — the choice is the operator's, not ours.
//
// Latched: on a mismatch page several parallel data calls 403
// together; only the first drives navigation. Auth routes are left
// alone — /me and /me/auth-state are exempt server-side, so a 403
// here while on /auth/* means the login flow itself is mid-flight
// and navigation would tear it down.
let workspaceRedirectLatch = false

// Exposed for tests — the latch is module state and must not bleed
// between cases.
export function resetWorkspaceRedirectLatch(): void {
  workspaceRedirectLatch = false
}

function redirectToSessionWorkspace(): void {
  if (workspaceRedirectLatch) return
  if (window.location.pathname.startsWith('/auth/')) return
  workspaceRedirectLatch = true
  const next = `${window.location.pathname}${window.location.search}`
  window.location.href = `/auth/wrong-workspace?next=${encodeURIComponent(next)}`
}

/** Unified API client — handles CSRF, credentials, auto-refresh, Sentry */
export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  // DELETE accepts an optional body — the staff console uses it to
  // carry an audit reason. Most callers will pass nothing.
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE', body }),
  // postBlob is the binary-download counterpart to post<T>. Used by
  // the PDF export today; future ICS/CSV exports can share the same
  // method. Returns the raw blob + filename pulled from the
  // Content-Disposition header so the caller can name the download.
  postBlob: (path: string, body?: unknown, opts?: RequestOptions) =>
    requestBlob(path, { ...opts, method: 'POST', body }),
  // getBlob is the GET counterpart to postBlob — for no-body download
  // endpoints (e.g. the DPIA PDF export). Same 401-refresh + filename
  // handling as postBlob.
  getBlob: (path: string, opts?: RequestOptions) => requestBlob(path, { ...opts, method: 'GET' }),
  // postRaw POSTs a File/Blob body verbatim (the file's own Content-Type,
  // no JSON encoding) and parses a typed JSON response. Used by the
  // encrypted-upload path where the request body IS the raw file.
  postRaw: <T>(path: string, file: File) => requestRaw<T>(path, file),
  // probe is a non-redirecting, non-retrying GET/POST for callers (router
  // guards, useIsStaff) that need to branch on the auth outcome themselves
  // instead of letting the client hard-redirect. See probe() above.
  probe: (path: string, opts?: RequestOptions) => probe(path, opts ?? {}),
}
